import type { XpTrack } from '@/lib/xpTrack';
import { useState, useEffect, useCallback } from 'react';
import { useAuditLog } from './useAuditLog';
import { collection, query, getDocs, getDoc, addDoc, updateDoc, deleteDoc, setDoc, doc, orderBy, Timestamp, increment, where } from 'firebase/firestore';
import { httpsCallableFromURL } from 'firebase/functions';
import { db, functions, callableUrl } from '@/lib/firebase';
import { getRoleConfig } from '@/lib/roleConfig';
import type { Badge, Challenge, BadgeRarity, ChallengeType, ChallengeRewards, PointTransaction, Level } from '@/types';

// ... (keep existing code until checkChallenge)




// ... (keep existing code until checkChallenge)

/**
 * Hook for managing the gamification system (Badges, Challenges, Points).
 * Provides functions to CRUD badges/challenges, award points, and check challenge completion.
 * 
 * @returns Object containining gamification state and operation functions.
 */
export const useGamification = () => {
    const { logAction } = useAuditLog();
    const [badges, setBadges] = useState<Badge[]>([]);
    const [challenges, setChallenges] = useState<Challenge[]>([]);
    const [levels, setLevels] = useState<Level[]>([]);
    const [loading, setLoading] = useState(true);

    /**
     * Helper to check if a user is allowed to participate in gamification.
     * Excludes certain roles (e.g., 'Parent') based on configuration.
     * @param userId The UID of the user to check.
     */
    const isUserGamified = async (userId: string): Promise<boolean> => {
        // Super-admins from `systemSettings/roleConfig` always participate
        // (Task #15 — `'162p0Oib...'` literal was here pre-#15).
        if (getRoleConfig().superAdminUids.includes(userId)) return true;

        // Emergency whitelist for staff who also participate as students.
        // These are dual-role UIDs (staff who also train) and remain
        // here as literals — separate concern from #15's super-admin
        // externalization. TODO: extract into a similar config field
        // (e.g. `dualRoleStudentUids`) in a follow-up task.
        const STUDENT_WHITELIST = [
            'wzuZOIiMz4OYYoePWkit7O8MzYv2',
            'IT0Ym31B8UMXdBFuOf3A5lNfQ9m2',
        ];
        if (STUDENT_WHITELIST.includes(userId)) return true;

        try {
            const userDoc = await getDoc(doc(db, 'users', userId));
            if (!userDoc.exists()) return false;
            const userData = userDoc.data();

            // Gamified if ANY role they hold is gamified — a union, not the single
            // `roleId`. Ahmed, 2026-09-13: "if someone trains as a student, they earn
            // as a student, whatever else they also do at the academy."
            //
            // The Coach role carries `isGamified: false`, so reading one role and
            // returning it made every coach-who-also-trains permanently unrankable —
            // Shady Tarek (4,570 XP, the only member who posts to the feed), Haitham
            // Mohamed and Mohamed Seif El Din among them. That is the same multi-role
            // unfairness we removed from roleChecks, expressed as a config flag.
            //
            // Same shape as the server-side roleResolution.ts: collect every role id,
            // resolve each to its document, and let one gamified role be enough.
            const ids: string[] = [];
            if (Array.isArray(userData.roleIds)) {
                ids.push(...userData.roleIds.filter((r: unknown): r is string => typeof r === 'string'));
            }
            if (typeof userData.roleId === 'string') ids.push(userData.roleId);

            if (ids.length) {
                const docs = await Promise.all(
                    [...new Set(ids)].map((id) => getDoc(doc(db, 'roles', id)).catch(() => null)),
                );
                let sawFlag = false;
                for (const rd of docs) {
                    if (!rd || !rd.exists()) continue;
                    const flag = (rd.data() as { isGamified?: boolean }).isGamified;
                    if (flag === undefined) continue;
                    sawFlag = true;
                    if (flag === true) return true;   // one gamified role is enough
                }
                // Every resolved role explicitly says no (e.g. Parent-only, Coach-only).
                if (sawFlag) return false;
            }

            // No role documents resolved — fall back to the legacy single-role string.
            const role = typeof userData.role === 'string' ? userData.role.toLowerCase() : '';
            if (role === 'parent') return false;
            return true;
        } catch (err) {
            console.error('Error checking gamification status:', err);
            return true;
        }
    };

    // Helper to convert Firestore timestamps
    const toDate = (val: unknown): Date => {
        if (!val) return new Date();
        if (typeof val === 'object' && val !== null && 'toDate' in val && typeof (val as { toDate: () => Date }).toDate === 'function') {
            return (val as { toDate: () => Date }).toDate();
        }
        if (val instanceof Date) return val;
        return new Date(val as string | number);
    };

    // Fetch badges
    const fetchBadges = useCallback(async () => {
        try {
            const q = query(collection(db, 'badges'), orderBy('name'));
            const snapshot = await getDocs(q);
            const fetched: Badge[] = [];
            snapshot.forEach(doc => {
                const data = doc.data();
                fetched.push({
                    id: doc.id,
                    name: data.name,
                    description: data.description,
                    icon: data.icon,
                    color: data.color,
                    rarity: data.rarity as BadgeRarity,
                    createdAt: toDate(data.createdAt),
                });
            });
            setBadges(fetched);
        } catch (err) {
            console.error('Error fetching badges:', err);
        }
    }, []);

    // Fetch challenges
    const fetchChallenges = useCallback(async () => {
        try {
            const q = query(collection(db, 'challenges'), orderBy('name'));
            const snapshot = await getDocs(q);
            const fetched: Challenge[] = [];
            snapshot.forEach(doc => {
                const data = doc.data();
                const rewards: ChallengeRewards = data.rewards || {
                    badgeId: data.badgeId // Fallback for legacy data
                };

                fetched.push({
                    id: doc.id,
                    name: data.name,
                    description: data.description,
                    type: data.type as ChallengeType,
                    targetValue: data.targetValue,
                    rewards: rewards,
                    badgeId: rewards.badgeId, // Keep for backward compat
                    isActive: data.isActive ?? true,
                    expiresAt: data.expiresAt ? toDate(data.expiresAt) : undefined,
                    createdAt: toDate(data.createdAt),
                });
            });
            setChallenges(fetched);
        } catch (err) {
            console.error('Error fetching challenges:', err);
        }
    }, []);

    // Fetch levels
    const fetchLevels = useCallback(async () => {
        try {
            const q = query(collection(db, 'levels'), orderBy('level'));
            const snapshot = await getDocs(q);
            const fetched: Level[] = [];
            snapshot.forEach(doc => {
                const data = doc.data();
                fetched.push({
                    id: doc.id,
                    level: data.level,
                    name: data.name,
                    pointsRequired: data.pointsRequired,
                    rewards: data.rewards,
                    icon: data.icon,
                    color: data.color,
                    createdAt: toDate(data.createdAt),
                });
            });
            setLevels(fetched);
        } catch (err) {
            console.error('Error fetching levels:', err);
        }
    }, []);

    useEffect(() => {
        const fetchAll = async () => {
            setLoading(true);
            await Promise.all([fetchBadges(), fetchChallenges(), fetchLevels()]);
            setLoading(false);
        };
        fetchAll();
    }, [fetchBadges, fetchChallenges, fetchLevels]);

    // Badge CRUD
    const createBadge = useCallback(async (badge: Omit<Badge, 'id' | 'createdAt'>) => {
        const docRef = await addDoc(collection(db, 'badges'), {
            ...badge,
            createdAt: Timestamp.now(),
        });
        await fetchBadges();
        return docRef.id;
    }, [fetchBadges]);

    const updateBadge = useCallback(async (id: string, updates: Partial<Badge>) => {
        await updateDoc(doc(db, 'badges', id), updates);
        await fetchBadges();
    }, [fetchBadges]);

    const deleteBadge = useCallback(async (id: string) => {
        await deleteDoc(doc(db, 'badges', id));
        await fetchBadges();
    }, [fetchBadges]);

    // Challenge CRUD
    const createChallenge = useCallback(async (challenge: Omit<Challenge, 'id' | 'createdAt'>) => {
        // Clean undefined values from rewards to avoid Firebase errors
        const cleanRewards: Record<string, unknown> = {};
        if (challenge.rewards) {
            if (challenge.rewards.badgeId) cleanRewards.badgeId = challenge.rewards.badgeId;
            if (challenge.rewards.walletBalance) cleanRewards.walletBalance = challenge.rewards.walletBalance;
            if (challenge.rewards.classCredit) cleanRewards.classCredit = challenge.rewards.classCredit;
            if (challenge.rewards.scorePoints !== undefined) cleanRewards.scorePoints = challenge.rewards.scorePoints;
        }

        const data: Record<string, unknown> = {
            name: challenge.name,
            description: challenge.description,
            type: challenge.type,
            targetValue: challenge.targetValue,
            isActive: challenge.isActive ?? true,
            rewards: Object.keys(cleanRewards).length > 0 ? cleanRewards : null,
            createdAt: Timestamp.now(),
        };

        // Only add optional fields if they exist
        if (challenge.badgeId) data.badgeId = challenge.badgeId;
        if (challenge.expiresAt) data.expiresAt = Timestamp.fromDate(challenge.expiresAt);

        const docRef = await addDoc(collection(db, 'challenges'), data);
        await fetchChallenges();
        return docRef.id;
    }, [fetchChallenges]);

    const updateChallenge = useCallback(async (id: string, updates: Partial<Challenge>) => {
        const data: Record<string, unknown> = { ...updates };
        if (updates.expiresAt) {
            data.expiresAt = Timestamp.fromDate(updates.expiresAt);
        }
        await updateDoc(doc(db, 'challenges', id), data);
        await fetchChallenges();
    }, [fetchChallenges]);

    const deleteChallenge = useCallback(async (id: string) => {
        await deleteDoc(doc(db, 'challenges', id));
        await fetchChallenges();
    }, [fetchChallenges]);

    // Level CRUD
    const createLevel = useCallback(async (levelData: Omit<Level, 'id' | 'createdAt'>) => {
        const data: Record<string, unknown> = {
            level: levelData.level,
            name: levelData.name,
            pointsRequired: levelData.pointsRequired,
            icon: levelData.icon || '⭐',
            color: levelData.color || '#3B82F6',
            createdAt: Timestamp.now(),
        };
        if (levelData.rewards) {
            const cleanRewards: Record<string, unknown> = {};
            if (levelData.rewards.badgeId) cleanRewards.badgeId = levelData.rewards.badgeId;
            if (levelData.rewards.walletBalance) cleanRewards.walletBalance = levelData.rewards.walletBalance;
            if (levelData.rewards.classCredit) cleanRewards.classCredit = levelData.rewards.classCredit;
            if (levelData.rewards.scorePoints !== undefined) cleanRewards.scorePoints = levelData.rewards.scorePoints;
            if (Object.keys(cleanRewards).length > 0) data.rewards = cleanRewards;
        }
        const docRef = await addDoc(collection(db, 'levels'), data);
        await fetchLevels();
        return docRef.id;
    }, [fetchLevels]);

    const updateLevel = useCallback(async (id: string, updates: Partial<Level>) => {
        await updateDoc(doc(db, 'levels', id), updates);
        await fetchLevels();
    }, [fetchLevels]);

    const deleteLevel = useCallback(async (id: string) => {
        await deleteDoc(doc(db, 'levels', id));
        await fetchLevels();
    }, [fetchLevels]);

    // Get user's current level based on points
    const getUserLevel = useCallback((points: number): { current: Level | null; next: Level | null; progress: number } => {
        if (levels.length === 0) return { current: null, next: null, progress: 0 };

        // Sort levels by pointsRequired ascending
        const sortedLevels = [...levels].sort((a, b) => a.pointsRequired - b.pointsRequired);

        let currentLevel: Level | null = null;
        let nextLevel: Level | null = null;

        for (let i = 0; i < sortedLevels.length; i++) {
            if (points >= sortedLevels[i].pointsRequired) {
                currentLevel = sortedLevels[i];
                nextLevel = sortedLevels[i + 1] || null;
            } else {
                break;
            }
        }

        // Calculate progress percentage to next level
        let progress = 100;
        if (currentLevel && nextLevel) {
            const pointsInCurrentLevel = points - currentLevel.pointsRequired;
            const pointsNeededForNext = nextLevel.pointsRequired - currentLevel.pointsRequired;
            progress = Math.min(100, Math.round((pointsInCurrentLevel / pointsNeededForNext) * 100));
        } else if (!currentLevel && sortedLevels.length > 0) {
            // User hasn't reached first level yet
            currentLevel = null;
            nextLevel = sortedLevels[0];
            progress = Math.round((points / sortedLevels[0].pointsRequired) * 100);
        }

        return { current: currentLevel, next: nextLevel, progress };
    }, [levels]);

    // Seed default levels
    const seedDefaultLevels = useCallback(async () => {
        const defaultLevels = [
            { level: 1, name: 'New Member', pointsRequired: 0, icon: '🌱', color: '#9CA3AF' },
            { level: 2, name: 'Active Member', pointsRequired: 1000, icon: '🌿', color: '#10B981' },
            { level: 3, name: 'Committed Member', pointsRequired: 3000, icon: '💪', color: '#22C55E' },
            { level: 4, name: 'Consistent Member', pointsRequired: 6000, icon: '🛡️', color: '#3B82F6' },
            { level: 5, name: 'Dedicated Member', pointsRequired: 10000, icon: '💰', color: '#6366F1' },
            { level: 6, name: 'Advanced Member', pointsRequired: 15000, icon: '📈', color: '#8B5CF6' },
            { level: 7, name: 'Core Member', pointsRequired: 21000, icon: '⭐', color: '#A855F7' },
            { level: 8, name: 'Pillar of ADJJA', pointsRequired: 28000, icon: '🏛️', color: '#EC4899' },
            { level: 9, name: 'Veteran Member', pointsRequired: 36000, icon: '👑', color: '#F97316' },
            { level: 10, name: 'ADJJA Inner Circle', pointsRequired: 46000, icon: '🔥', color: '#EF4444' },
        ];

        try {
            for (const lvl of defaultLevels) {
                const levelId = `level_${lvl.level}`;
                await setDoc(doc(db, 'levels', levelId), {
                    ...lvl,
                    createdAt: Timestamp.now()
                }, { merge: true });
            }
            await fetchLevels();
            return true;
        } catch (err) {
            console.error('Error seeding levels:', err);
            return false;
        }
    }, [fetchLevels]);

    // Get badge by ID
    const getBadgeById = useCallback((id: string) => badges.find(b => b.id === id), [badges]);

    // Manual Points Adjustment
    const adjustPoints = useCallback(async (
        userId: string, amount: number, description: string, moderatorId: string,
        options: { requestId?: string; track: XpTrack },
    ): Promise<{ duplicate: boolean }> => {
        try {
            // Check if user is allowed to participate
            const isGamified = await isUserGamified(userId);
            if (!isGamified) {
                throw new Error('User belongs to a role that is excluded from gamification.');
            }

            // Get user's current score BEFORE update (for level-up detection)
            const userRef = doc(db, 'users', userId);
            const userSnap = await getDoc(userRef);
            const userData = userSnap.data();
            const oldScore = userData?.totalScore || 0;
            const newScore = oldScore + amount;
            const userName = userData?.displayName || 'A member';

            // 1+2. Award points via the awardPoints Cloud Function callable
            // (Task #22). The callable atomically writes pointTransactions
            // and increments users/{userId}.totalScore via admin SDK so the
            // Firestore rules can deny client direct writes.
            const awardPointsCallable = httpsCallableFromURL<
                { userId: string; amount: number; type: 'manual_adjustment'; description: string; moderatorId?: string; requestId?: string; track: XpTrack },
                { success: boolean; transactionId: string; newTotalScore: number; duplicate?: boolean }
            >(functions, callableUrl('awardPoints'));
            const awarded = await awardPointsCallable({
                userId,
                amount,
                type: 'manual_adjustment',
                description,
                moderatorId,
                track: options.track,
                ...(options.requestId ? { requestId: options.requestId } : {}),
            });

            // A retried submission the server had already paid. Stop here: everything
            // below (the leaderboard increment, audit row, notifications) already happened
            // the first time, and repeating the leaderboard increment would double it.
            if (awarded.data?.duplicate) return { duplicate: true };

            // 1b. Update Monthly Leaderboard — Training XP only (xpTrack.ts). A Lifetime-only
            // award raises totalScore (levels) but never the board.
            if (options.track === 'training') {
                try {
                    const now = new Date();
                    const periodId = `monthly_${now.getFullYear()}_${String(now.getMonth() + 1).padStart(2, '0')}`;
                    await setDoc(doc(db, 'leaderboards', periodId, 'scores', userId), {
                        userId,
                        score: increment(amount),
                        updatedAt: Timestamp.now()
                    }, { merge: true });
                } catch (lErr) {
                    console.error('Error updating leaderboard:', lErr);
                }
            }

            await logAction({
                action: 'adjust_points',
                module: 'Gamification',
                targetId: userId,
                details: `Adjusted points by ${amount}: ${description}`
            });

            // 3. Send Notification to user
            //
            // Best-effort write: firestore.rules currently only permit
            // notifications.create when `userId === request.auth.uid` AND
            // the type is in a small whitelist (support_request_submitted,
            // feedback_submitted, self_reminder). Admin-initiated XP
            // notifications don't satisfy either condition, so this write
            // throws PERMISSION_DENIED. We swallow the error here so the
            // user flow isn't blocked. Proper fix (#22.6): move notification
            // creation server-side into the awardPoints callable so it
            // runs under admin SDK and bypasses the client rule.
            if (amount !== 0) {
                try {
                    await addDoc(collection(db, 'notifications'), {
                        userId,
                        type: 'achievement',
                        title: amount > 0 ? 'XP Earned' : 'XP Deducted',
                        message: `You ${amount > 0 ? 'earned' : 'lost'} ${Math.abs(amount)} XP: ${description}`,
                        link: '/leaderboard',
                        isRead: false,
                        createdAt: Timestamp.now(),
                        metadata: {
                            actorId: moderatorId,
                        }
                    });
                } catch (notifErr) {
                    console.warn('XP notification create denied (expected pending #22.6 server-side move):', notifErr);
                }

                // 3b. Notify parent if child earned XP (positive only)
                if (amount > 0 && userData?.parentId) {
                    try {
                        await addDoc(collection(db, 'notifications'), {
                            userId: userData.parentId,
                            type: 'child_achievement',
                            title: `${userName} earned XP! 🎉`,
                            message: `${userName} earned ${amount} XP: ${description}`,
                            link: `/profile/${userId}`,
                            isRead: false,
                            createdAt: Timestamp.now(),
                            metadata: {
                                childId: userId,
                                childName: userName,
                                xpAmount: amount
                            }
                        });
                    } catch (parentNotifErr) {
                        console.warn('Parent XP notification create denied (expected pending #22.6 server-side move):', parentNotifErr);
                    }
                }
            }

            // 4. Check for Level Up and notify followers
            if (levels.length > 0 && amount > 0) {
                const sortedLevels = [...levels].sort((a, b) => a.pointsRequired - b.pointsRequired);

                // Find old level
                let oldLevel = sortedLevels[0];
                for (const lvl of sortedLevels) {
                    if (oldScore >= lvl.pointsRequired) oldLevel = lvl;
                    else break;
                }

                // Find new level
                let newLevel = sortedLevels[0];
                for (const lvl of sortedLevels) {
                    if (newScore >= lvl.pointsRequired) newLevel = lvl;
                    else break;
                }

                // If leveled up, notify followers
                //
                // Best-effort writes for the same reason as the XP
                // notifications above — client rules deny notifications.create
                // for non-self / non-whitelisted-type. Each addDoc is wrapped
                // independently so one failure doesn't cascade. Proper fix
                // tracked as #22.6.
                if (newLevel.level > oldLevel.level) {
                    // Query all followers of this user
                    const followsQuery = query(
                        collection(db, 'follows'),
                        where('followingId', '==', userId)
                    );
                    const followsSnap = await getDocs(followsQuery);

                    // Send notification to each follower
                    const followerNotifications = followsSnap.docs.map(async (followDoc) => {
                        const followerData = followDoc.data();
                        const followerId = followerData.followerId;

                        try {
                            await addDoc(collection(db, 'notifications'), {
                                userId: followerId,
                                type: 'level_up',
                                title: '🎉 Level Up!',
                                message: `${userName} reached Level ${newLevel.level}: ${newLevel.name}!`,
                                link: `/u/${userId}`,
                                isRead: false,
                                createdAt: Timestamp.now(),
                                metadata: {
                                    actorId: userId,
                                    actorName: userName,
                                    newLevel: newLevel.level,
                                    levelName: newLevel.name,
                                }
                            });
                        } catch (followerNotifErr) {
                            console.warn('Follower level-up notification denied (expected pending #22.6):', followerNotifErr);
                        }
                    });

                    await Promise.all(followerNotifications);

                    // Also notify the user themselves about their level up
                    try {
                        await addDoc(collection(db, 'notifications'), {
                            userId,
                            type: 'level_up',
                            title: '🎉 Level Up!',
                            message: `Congratulations! You reached Level ${newLevel.level}: ${newLevel.name}!`,
                            link: '/leaderboard',
                            isRead: false,
                            createdAt: Timestamp.now(),
                            metadata: {
                                newLevel: newLevel.level,
                                levelName: newLevel.name,
                            }
                        });
                    } catch (selfLevelUpErr) {
                        console.warn('Self level-up notification denied (expected pending #22.6):', selfLevelUpErr);
                    }
                }
            }

            return { duplicate: false };
        } catch (err) {
            console.error('Error adjusting points:', err);
            throw err;
        }
    }, [levels]);

    const fetchPointHistory = useCallback(async (userId: string) => {
        try {
            const q = query(
                collection(db, 'pointTransactions'),
                where('userId', '==', userId)
                // orderBy removed to avoid composite index requirement
            );
            const snapshot = await getDocs(q);
            const transactions = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                createdAt: toDate(doc.data().createdAt)
            })) as PointTransaction[];

            // Sort in memory
            return transactions.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
        } catch (err) {
            console.error('Error fetching point history:', err);
            return [];
        }
    }, []);

    const fetchUserBadges = useCallback(async (userId: string) => {
        try {
            const q = query(
                collection(db, 'userBadges'),
                where('userId', '==', userId),
                orderBy('earnedAt', 'desc')
            );
            const snapshot = await getDocs(q);
            const userBadges = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                earnedAt: toDate(doc.data().earnedAt),
                badgeId: doc.data().badgeId as string, // Ensure typed
            }));

            // Join with badge details
            const detailedBadges = userBadges.map(ub => {
                const badgeDef = badges.find(b => b.id === ub.badgeId);
                return {
                    ...ub,
                    badge: badgeDef
                };
            });

            return detailedBadges;
        } catch (err) {
            console.error('Error fetching user badges:', err);
            return [];
        }
    }, [badges]);

    // Check and trigger challenge completion
    const checkChallenge = useCallback(async (challengeId: string, userId: string, uniqueKey?: string, additionalDetails?: string) => {
        try {
            // 1. Get Challenge
            let challengeDoc = await getDoc(doc(db, 'challenges', challengeId));

            // Auto-create core challenges if they don't exist
            if (!challengeDoc.exists()) {
                const coreDefaults: Record<string, { name: string; description: string; type: string; scorePoints: number }> = {
                    'account_created': { name: 'Welcome to the Academy', description: 'Complete your account registration.', type: 'account_created', scorePoints: 100 },
                    'profile_completed': { name: 'Profile Perfection', description: 'Complete 100% of your profile information.', type: 'profile_completed', scorePoints: 100 },
                    'first_checkin': { name: 'First Step on the Mat', description: 'Check in to your very first class.', type: 'first_checkin', scorePoints: 50 },
                    'grip_someone': { name: 'Making Connections', description: 'Grip (follow) another member.', type: 'grip_someone', scorePoints: 10 },
                    'get_gripped': { name: 'Growing Influence', description: 'Get gripped by another member.', type: 'get_gripped', scorePoints: 20 },
                    'regular_checkin': { name: 'Class Attendance', description: 'Attended a class/session.', type: 'check_in', scorePoints: 10 },
                    // 'self_checkin_bonus' is deliberately NOT auto-created here.
                    //
                    // The trigger for it ships (useAttendance), but a challenge that
                    // does not exist makes checkChallenge return early — so the bonus
                    // is DORMANT until an admin creates it in Settings > Gamification.
                    // That is intentional: a hidden incentive is not an incentive, and
                    // members comparing totals would see 10 vs 15 with no explanation.
                    // It gets switched on in the same batch as the check-in button copy
                    // ("Check in (+15 XP)") that Ahmed approves.
                    //
                    // When created it must be type 'custom', NOT 'check_in':
                    // markAttendance loops over every active check_in challenge and
                    // would otherwise pay the bonus to members their coach marked.
                };

                const coreChallenge = coreDefaults[challengeId];
                if (coreChallenge) {
                    console.log('Auto-creating missing core challenge:', challengeId);
                    await setDoc(doc(db, 'challenges', challengeId), {
                        name: coreChallenge.name,
                        description: coreChallenge.description,
                        type: coreChallenge.type,
                        targetValue: 1,
                        rewards: { scorePoints: coreChallenge.scorePoints },
                        isActive: true,
                        createdAt: Timestamp.now()
                    });
                    // Refetch the newly created challenge
                    challengeDoc = await getDoc(doc(db, 'challenges', challengeId));
                } else {
                    console.log('Challenge not found and not a core challenge:', challengeId);
                    return;
                }
            }

            // Check if user is allowed to participate
            const isGamified = await isUserGamified(userId);
            if (!isGamified) {
                console.log('User excluded from gamification');
                return;
            }

            const challenge = challengeDoc.data() as Challenge;

            if (!challenge.isActive) return;

            // 2. Check if already completed
            // check 'userChallenges' collection for { userId, challengeId }
            let q = query(
                collection(db, 'userChallenges'),
                where('userId', '==', userId),
                where('challengeId', '==', challengeId)
            );

            // If uniqueKey provided, check for that specific instance
            if (uniqueKey) {
                q = query(
                    collection(db, 'userChallenges'),
                    where('userId', '==', userId),
                    where('challengeId', '==', challengeId),
                    where('uniqueKey', '==', uniqueKey)
                );
            }

            const snapshot = await getDocs(q);

            // Logic:
            // If uniqueKey is provided, we only block if that key exists.
            // If uniqueKey is NOT provided, we block if ANY record exists (assuming single-completion by default).
            // This preserves backward compatibility for existing 1-time challenges.
            if (!snapshot.empty) {
                // Double check for the 'no uniqueKey' case to be sure we are blocking correctly?
                // The query above handles it.
                console.log('Challenge already completed (or this instance/period completed).');
                return;
            }

            // Fetch user data for parent notification
            const userDoc = await getDoc(doc(db, 'users', userId));
            const userData = userDoc.exists() ? userDoc.data() : null;
            const userName = userData?.displayName || 'Your child';
            const parentId = userData?.parentId;

            // 3. Award rewards + mark completed via the completeChallenge
            // Cloud Function callable (Task #22.5). The callable atomically:
            //   - re-validates the challenge exists + is active
            //   - re-dedups against userChallenges (race-safe inside a tx)
            //   - applies scorePoints / badgeId / walletBalance rewards
            //   - writes the userChallenges completion doc
            //
            // The local dedup query above is a UX-latency optimization: the
            // server re-checks, so a client-side false-negative here just
            // adds a round-trip.
            //
            // TODO(#22.6): achievement + child_achievement + level_up
            // notifications + leaderboard score increment all move to an
            // onChallengeCompleted Firestore trigger. Until then, in-app
            // notifications for challenge completions are missing — the
            // post-trigger user feedback is "your totalScore went up".
            try {
                const completeChallengeCallable = httpsCallableFromURL<
                    { userId: string; challengeId: string; metadata?: Record<string, unknown> },
                    {
                        success: boolean;
                        alreadyCompleted: boolean;
                        rewardsGranted: Record<string, unknown>;
                    }
                >(functions, callableUrl('completeChallenge'));

                const result = await completeChallengeCallable({
                    userId,
                    challengeId,
                    metadata: uniqueKey ? { uniqueKey } : undefined,
                });

                if (!result.data.alreadyCompleted) {
                    const pts = challenge.rewards?.scorePoints ?? 0;
                    await logAction({
                        action: 'challenge_complete',
                        module: 'Gamification',
                        targetId: userId,
                        details: `Completed challenge ${challenge.name}${pts ? ` (+${pts} XP)` : ''}${additionalDetails ? `. ${additionalDetails}` : ''}`,
                    });
                }
            } catch (callErr) {
                console.error('completeChallenge callable failed:', callErr);
                throw callErr;
            }
            // Silence unused vars from removed pre-migration logic.
            void userName; void parentId;
        } catch (err) {
            console.error('Error checking challenge:', err);
            throw err;
        }
    }, []);

    // Seed Default Challenges
    const seedDefaultChallenges = useCallback(async () => {
        const defaultChallenges = [
            {
                id: 'account_created',
                name: 'Welcome to the Academy',
                description: 'Complete your account registration.',
                type: 'account_created',
                targetValue: 1,
                rewards: { scorePoints: 100 },
                isActive: true
            },
            {
                id: 'profile_completed',
                name: 'Profile Perfection',
                description: 'Complete 100% of your profile information.',
                type: 'profile_completed',
                targetValue: 1,
                rewards: { scorePoints: 100 },
                isActive: true
            },
            {
                id: 'first_checkin',
                name: 'First Step on the Mat',
                description: 'Check in to your very first class.',
                type: 'first_checkin',
                targetValue: 1,
                rewards: { scorePoints: 50 },
                isActive: true
            },
            {
                id: 'monthly_attendance_8',
                name: 'Consistent Practitioner',
                description: 'Attend 8 classes in a single month.',
                type: 'monthly_attendance',
                targetValue: 8,
                rewards: { scorePoints: 200 },
                isActive: true
            },
            {
                id: 'streak_12_weeks',
                name: 'Iron Will Streak',
                description: 'Attend at least one class per week for 12 consecutive weeks.',
                type: 'streak',
                targetValue: 12,
                rewards: { scorePoints: 500 },
                isActive: true
            },
            {
                id: 'course_completed',
                name: 'Knowledge Seeker',
                description: 'Complete an LMS course.',
                type: 'course_completed',
                targetValue: 1,
                rewards: { scorePoints: 150 },
                isActive: true
            },
            {
                id: 'grip_someone',
                name: 'Making Connections',
                description: 'Grip (follow) another member of the academy.',
                type: 'grip_someone',
                targetValue: 1,
                rewards: { scorePoints: 10 },
                isActive: true,
                isRepeatable: true
            },
            {
                id: 'get_gripped',
                name: 'Growing Influence',
                description: 'Get gripped (followed) by another member.',
                type: 'get_gripped',
                targetValue: 1,
                rewards: { scorePoints: 20 },
                isActive: true,
                isRepeatable: true
            },
            {
                id: 'self_feedback',
                name: 'Self Reflection',
                description: 'Rate your own performance after a class.',
                type: 'self_feedback',
                targetValue: 1,
                rewards: { scorePoints: 10 },
                isActive: true,
                isRepeatable: true
            },
            {
                id: 'peer_feedback',
                name: 'Team Player',
                description: 'Rate a peer\'s performance after class. Earn 10 XP per peer (max 30 XP for 3 peers).',
                type: 'peer_feedback',
                targetValue: 3,
                rewards: { scorePoints: 30 },
                isActive: true,
                isRepeatable: true
            }
        ];

        try {
            for (const challenge of defaultChallenges) {
                const { id, ...data } = challenge;
                // Use setDoc with merge: true to avoid overwriting existing tracking if any
                await setDoc(doc(db, 'challenges', id), {
                    ...data,
                    createdAt: Timestamp.now() // This will update createdAt every time, maybe check existence first?
                    // Actually, for a reset/seed, updating timestamp is acceptable or we can just merge fields.
                    // Let's not overwrite createdAt if possible, but simplest is just upsert.
                }, { merge: true });
            }
            await fetchChallenges();
            return true;
        } catch (err) {
            console.error('Error seeding challenges:', err);
            return false;
        }
    }, [fetchChallenges]);

    return {
        badges,
        challenges,
        levels,
        loading,
        // Badge operations
        createBadge,
        updateBadge,
        deleteBadge,
        getBadgeById,
        // Challenge operations
        createChallenge,
        updateChallenge,
        deleteChallenge,
        checkChallenge,
        seedDefaultChallenges,
        // Level operations
        createLevel,
        updateLevel,
        deleteLevel,
        getUserLevel,
        seedDefaultLevels,
        // Points operations
        adjustPoints,
        fetchPointHistory,
        // User specifics
        fetchUserBadges,
        // Refresh
        refreshBadges: fetchBadges,
        refreshChallenges: fetchChallenges,
        refreshLevels: fetchLevels,
    };
};

